import {
  Component,
  ChangeDetectionStrategy,
  signal,
  inject,
  DestroyRef,
  OnInit,
  computed,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { TranslateModule } from "@ngx-translate/core";
import {
  FieldType,
  FieldTypeConfig,
  FormlyFieldConfig,
  FormlyModule,
} from "@ngx-formly/core";
import { startWith, debounceTime } from "rxjs/operators";
import { firstErrorMessage } from "../../forms/formly-errors";

const TAB_BASE =
  "px-4 py-2 rounded-full font-alternate text-[13px] font-medium transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-1.5";
const TAB_ACTIVE = "bg-mty-primary text-white shadow-sm";
const TAB_IDLE = "text-black/60 hover:bg-black/5";

interface FormError {
  id: string;
  label: string;
  messageKey: string;
  tabIndex: number;
  tabLabel: string;
}

@Component({
  selector: "app-formly-mat-tabs",
  standalone: true,
  imports: [CommonModule, MatIconModule, TranslateModule, FormlyModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col">
      <div class="sticky top-0 z-[2] bg-white px-6 md:px-8 pt-4 pb-4 border-b border-black/5 flex flex-wrap gap-1.5">
        @for (tab of tabs; track trackTab($index); let i = $index) {
          <button
            type="button"
            [class]="tabClass(i)"
            [attr.aria-selected]="selectedIndex() === i"
            (click)="onTabIndexChange(i)">
            {{ (tab.props?.label || '') | translate }}
            @if (badgeForTab(i); as b) {
              @if (b.kind === 'dot') {
                <span class="inline-block w-1.5 h-1.5 rounded-full bg-mty-danger" aria-label="has errors"></span>
              } @else if (b.kind === 'count') {
                <span
                  [class]="badgeClass(i)"
                  [attr.aria-label]="b.count + ' errors'">
                  {{ b.count }}
                </span>
              }
            }
          </button>
        }
      </div>

      @if (showSummary()) {
        <div class="mx-6 md:mx-8 mt-4 rounded-2xl border border-mty-danger/30 bg-mty-danger/5 p-4">
          <div class="flex items-start gap-3">
            <mat-icon class="text-mty-danger shrink-0" style="margin-top:2px">error_outline</mat-icon>
            <div class="flex-1 min-w-0">
              <p class="font-alternate text-[13px] font-semibold text-mty-danger m-0 mb-2">
                {{ errors().length }} {{ 'validation errors found — click to jump' | translate }}
              </p>
              <ul class="flex flex-col gap-1 m-0 list-none p-0">
                @for (err of errors(); track err.id) {
                  <li>
                    <button
                      type="button"
                      (click)="onErrorClick(err)"
                      class="text-left w-full font-base text-[13px] text-black/80 hover:text-mty-danger transition-colors">
                      <span class="font-alternate text-[10px] font-semibold text-black/45 uppercase tracking-wider mr-2">
                        {{ err.tabLabel | translate }}
                      </span>
                      <span class="font-semibold underline decoration-dotted underline-offset-2">
                        {{ err.label | translate }}
                      </span>
                      <span class="text-black/60 ml-1">— {{ err.messageKey | translate }}</span>
                    </button>
                  </li>
                }
              </ul>
            </div>
          </div>
        </div>
      }

      <div class="px-6 md:px-8 py-6">
        @for (tab of tabs; track trackTab($index); let i = $index) {
          <div [hidden]="selectedIndex() !== i" class="flex flex-col gap-5">
            <formly-field [field]="tab"></formly-field>
          </div>
        }
      </div>
    </div>
  `,
})
export class MatTabsFieldComponent
  extends FieldType<FieldTypeConfig>
  implements OnInit
{
  private readonly destroyRef = inject(DestroyRef);

  readonly selectedIndex = signal(0);
  private readonly visitedTabs = signal<Set<string>>(new Set());
  private readonly statusBump = signal(0);

  /**
   * Plain method (not a `computed`) — Angular re-runs it every change
   * detection pass, so it always reads fresh `touched` state. A
   * `computed` here would only re-run when an explicit signal it reads
   * updates, and no cheap root-level signal exists that reliably ticks
   * on individual child-control touched transitions.
   */
  errors(): FormError[] {
    // Read statusBump so Angular re-invokes this method (and the
    // template bindings that call it) when the form emits a
    // TouchedChangeEvent / ValueChangeEvent / StatusChangeEvent.
    void this.statusBump();
    const list: FormError[] = [];
    this.tabs.forEach((tab, tabIndex) => {
      const tabLabel = (tab.props?.label as string | undefined) ?? String(tabIndex + 1);
      collectInvalidLeaves(tab, (leaf) => {
        if (!leaf.formControl?.touched) return;
        list.push({
          id: String(leaf.id ?? ''),
          label: (leaf.props?.label as string | undefined) ?? String(leaf.key ?? ''),
          messageKey: firstErrorMessage(leaf.formControl),
          tabIndex,
          tabLabel,
        });
      });
    });
    return list;
  }

  showSummary(): boolean {
    return this.errors().length > 0;
  }

  get tabs(): FormlyFieldConfig[] {
    return this.field.fieldGroup ?? [];
  }

  ngOnInit(): void {
    // Tabs become "visited" only when the user actively clicks one — we
    // do NOT auto-mark the initial tab. That way a fresh dialog stays
    // quiet (no summary, no chips) until the user actually interacts.
    if (this.field.formControl) {
      // `events` emits on value / status / touched / pristine changes.
      // We need touched events too — the chip count & error summary are
      // gated on `touched`, and statusChanges alone doesn't fire when a
      // field that's already invalid simply gets touched.
      this.field.formControl.events
        .pipe(
          startWith(null),
          debounceTime(50),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(() => this.statusBump.update((n) => n + 1));
    }
  }

  trackTab(index: number): number {
    return index;
  }

  tabClass(index: number): string {
    return `${TAB_BASE} ${
      this.selectedIndex() === index ? TAB_ACTIVE : TAB_IDLE
    }`;
  }

  badgeClass(index: number): string {
    const base = 'inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-white text-[11px] font-semibold';
    return `${base} ${this.selectedIndex() === index ? 'bg-white/25' : 'bg-mty-danger'}`;
  }

  onTabIndexChange(index: number): void {
    this.selectedIndex.set(index);
    this.visitedTabs.update((s) => {
      s.add(String(index));
      return new Set(s);
    });
  }

  onErrorClick(err: FormError): void {
    if (this.selectedIndex() !== err.tabIndex) {
      this.selectedIndex.set(err.tabIndex);
      this.visitedTabs.update((s) => {
        s.add(String(err.tabIndex));
        return new Set(s);
      });
    }
    if (!err.id) return;
    // Double rAF: first frame runs after Angular CD flips [hidden]; the
    // second runs after the browser has laid the newly-shown tab out, so
    // the target element has a real bounding box for scrollIntoView.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const host = document.querySelector<HTMLElement>(
          `[data-field-id="${err.id}"]`,
        );
        if (!host) return;
        host.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusable = host.matches('input, textarea, select, button')
          ? host
          : host.querySelector<HTMLElement>(
              'input, textarea, select, button, [tabindex]:not([tabindex="-1"])',
            );
        focusable?.focus({ preventScroll: true });
      }),
    );
  }

  badgeForTab(
    index: number,
  ): { kind: "dot" | "count"; count?: number } | null {
    void this.statusBump();
    const tab = this.tabs[index];
    if (!tab) return null;
    const touchedInvalid = countTouchedInvalidDescendants(tab);
    if (touchedInvalid > 0) {
      return { kind: "count", count: touchedInvalid };
    }
    // No touched-invalid leaves — but if there's still structurally
    // invalid content (e.g. required empty fields) AND the tab has
    // never been visited, show a subtle dot as a heads-up.
    const total = countInvalidDescendants(tab);
    if (total === 0) return null;
    if (this.visitedTabs().has(String(index))) return null;
    return { kind: "dot" };
  }
}

function countInvalidDescendants(field: FormlyFieldConfig): number {
  let n = 0;
  collectInvalidLeaves(field, () => n++);
  return n;
}

function countTouchedInvalidDescendants(field: FormlyFieldConfig): number {
  let n = 0;
  collectInvalidLeaves(field, (leaf) => {
    if (leaf.formControl?.touched) n++;
  });
  return n;
}

function collectInvalidLeaves(
  field: FormlyFieldConfig,
  visit: (leaf: FormlyFieldConfig) => void,
): void {
  if (field.fieldGroup) {
    for (const child of field.fieldGroup) collectInvalidLeaves(child, visit);
    return;
  }
  if (field.formControl?.invalid) visit(field);
}
